import React from 'react';

/**
 * ErrorBoundary globale: cattura errori React in fase di rendering
 * e mostra un fallback diagnostico invece di pagina bianca.
 *
 * Tutti gli errori vengono anche salvati in `localStorage.jvservice_last_error`
 * così l'utente può recuperarli anche dopo un refresh (F12 → Console:
 * `localStorage.getItem('jvservice_last_error')`).
 */
class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, error: null, errorInfo: null };
  }

  static getDerivedStateFromError(error) {
    return { hasError: true, error };
  }

  componentDidCatch(error, errorInfo) {
    this.setState({ errorInfo });
    const payload = {
      timestamp: new Date().toISOString(),
      message: error?.message,
      stack: error?.stack,
      componentStack: errorInfo?.componentStack,
      url: window.location.href,
    };
    try {
      const history = JSON.parse(localStorage.getItem('jvservice_error_history') || '[]');
      history.unshift(payload);
      localStorage.setItem('jvservice_last_error', JSON.stringify(payload, null, 2));
      localStorage.setItem('jvservice_error_history', JSON.stringify(history.slice(0, 10)));
    } catch (e) {
      console.error('[ErrorBoundary] Unable to save error to localStorage', e);
    }
    console.error('[ErrorBoundary]', error, errorInfo);
  }

  handleReset = () => {
    this.setState({ hasError: false, error: null, errorInfo: null });
  };

  handleReload = () => {
    window.location.reload();
  };

  handleCopy = () => {
    const text = localStorage.getItem('jvservice_last_error') || JSON.stringify(this.state.error);
    navigator.clipboard?.writeText(text);
    alert('Errore copiato negli appunti. Incollalo allo sviluppatore per assistenza.');
  };

  render() {
    if (this.state.hasError) {
      return (
        <div style={{
          padding: 32, maxWidth: 900, margin: '40px auto',
          fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
        }}>
          <div style={{
            background: '#fff', border: '1px solid #fecaca',
            borderRadius: 12, padding: 24,
            boxShadow: '0 4px 20px rgba(0,0,0,0.06)',
          }}>
            <h1 style={{ color: '#b91c1c', marginTop: 0, fontSize: 22 }}>
              ⚠️ Si è verificato un errore
            </h1>
            <p style={{ color: '#475569' }}>
              L'applicazione ha riscontrato un problema. Puoi tornare indietro o ricaricare la pagina.
              Se il problema persiste, copia il dettaglio sotto e mandalo allo sviluppatore.
            </p>

            <div style={{
              background: '#f8fafc', border: '1px solid #e2e8f0',
              borderRadius: 8, padding: 12, marginTop: 16,
              fontFamily: 'monospace', fontSize: 13, color: '#0f172a',
              maxHeight: 280, overflow: 'auto', whiteSpace: 'pre-wrap',
            }}>
              <div style={{ fontWeight: 600, marginBottom: 6 }}>
                {this.state.error?.message || 'Errore sconosciuto'}
              </div>
              {this.state.error?.stack && (
                <div style={{ color: '#64748b', fontSize: 12 }}>
                  {String(this.state.error.stack).slice(0, 1500)}
                </div>
              )}
              {this.state.errorInfo?.componentStack && (
                <details style={{ marginTop: 8 }}>
                  <summary style={{ cursor: 'pointer', color: '#7c3aed' }}>
                    Component stack
                  </summary>
                  <div style={{ color: '#64748b', fontSize: 12, marginTop: 4 }}>
                    {String(this.state.errorInfo.componentStack).slice(0, 1500)}
                  </div>
                </details>
              )}
            </div>

            <div style={{ display: 'flex', gap: 8, marginTop: 16 }}>
              <button
                onClick={this.handleReset}
                style={{
                  padding: '8px 16px', borderRadius: 8,
                  background: '#7c3aed', color: 'white', border: 'none',
                  cursor: 'pointer', fontWeight: 500,
                }}
              >
                Riprova
              </button>
              <button
                onClick={this.handleReload}
                style={{
                  padding: '8px 16px', borderRadius: 8,
                  background: '#e2e8f0', color: '#334155', border: 'none',
                  cursor: 'pointer', fontWeight: 500,
                }}
              >
                Ricarica pagina
              </button>
              <button
                onClick={this.handleCopy}
                style={{
                  padding: '8px 16px', borderRadius: 8,
                  background: 'white', color: '#7c3aed',
                  border: '1px solid #7c3aed',
                  cursor: 'pointer', fontWeight: 500,
                }}
              >
                Copia errore
              </button>
            </div>

            <div style={{ marginTop: 16, fontSize: 12, color: '#94a3b8' }}>
              💡 Per recuperare l'errore anche dopo refresh: F12 → Console → digita{' '}
              <code style={{ background: '#f1f5f9', padding: '2px 6px', borderRadius: 4 }}>
                localStorage.getItem('jvservice_last_error')
              </code>
            </div>
          </div>
        </div>
      );
    }
    return this.props.children;
  }
}

export default ErrorBoundary;
